import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";
import { Quote, Plus, Trash2, GripVertical, Save, CheckCircle2, XCircle, Inbox } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

const TEAL = "#0D9488";
const TEAL_D = "#0F766E";
const emptyRow = () => ({ quote: "", name: "", role: "", initials: "" });

/**
 * Admin editor for the rotating landing testimonial strip.
 * Reads from `settings.landing_testimonials` (array of {quote, name, role, initials}),
 * offers add / delete / reorder, and renders a live preview using the exact same
 * styling as the public landing so admins see what visitors will see.
 */
export default function TestimonialsCard() {
  const { settings, refreshSettings } = useSettings();
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);
  const [queue, setQueue] = useState([]);
  const [reviewingId, setReviewingId] = useState(null);

  const loadQueue = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/testimonials/queue", { params: { status: "pending" } });
      setQueue(Array.isArray(data) ? data : []);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  useEffect(() => {
    const list = Array.isArray(settings?.landing_testimonials) ? settings.landing_testimonials : [];
    setRows(list.length ? list.map((r) => ({ ...emptyRow(), ...r })) : [emptyRow()]);
  }, [settings?.landing_testimonials]);

  const approveSubmission = async (sub) => {
    setReviewingId(sub.id);
    try {
      await api.post(`/admin/testimonials/queue/${sub.id}/approve`);
      toast.success(`Approved @${sub.user_handle} — now live on the landing carousel.`);
      await refreshSettings();
      loadQueue();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReviewingId(null);
    }
  };

  const rejectSubmission = async (sub) => {
    setReviewingId(sub.id);
    try {
      await api.post(`/admin/testimonials/queue/${sub.id}/reject`);
      toast.success(`Rejected @${sub.user_handle}.`);
      loadQueue();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReviewingId(null);
    }
  };

  const updateRow = (i, field, value) => {
    setRows((r) => r.map((row, k) => (k === i ? { ...row, [field]: value } : row)));
  };
  const addRow = () => setRows((r) => [...r, emptyRow()]);
  const removeRow = (i) => setRows((r) => (r.length === 1 ? [emptyRow()] : r.filter((_, k) => k !== i)));
  const move = (i, dir) => {
    setRows((r) => {
      const j = i + dir;
      if (j < 0 || j >= r.length) return r;
      const copy = r.slice();
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };

  const save = async () => {
    setSaving(true);
    // Drop empty rows before sending
    const clean = rows
      .map((r) => ({
        quote: (r.quote || "").trim(),
        name: (r.name || "").trim(),
        role: (r.role || "").trim(),
        initials: (r.initials || "").trim().slice(0, 3).toUpperCase(),
      }))
      .filter((r) => r.quote);
    try {
      await api.patch("/settings", { landing_testimonials: clean });
      await refreshSettings();
      toast.success(`Saved ${clean.length} testimonial${clean.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const previewList = rows.filter((r) => (r.quote || "").trim());
  const preview = previewList[previewIdx] || previewList[0] || emptyRow();
  const previewInitials = (preview.initials || (preview.name || "").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()) || "??";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="testimonials-card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Quote className="w-4 h-4" style={{ color: TEAL }} />
          <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>
            Testimonials
          </div>
        </div>
        <div className="text-[11px] text-slate-500">
          Rotates every 9s on the landing page
        </div>
      </div>
      <h3 className="font-display font-bold text-xl text-slate-900 mb-1">Testimonial Editor</h3>
      <p className="text-sm text-slate-500 mb-5">
        Add short quotes from users who track peptides + nutrition together. Blank rows are dropped on save.
      </p>

      {/* Pending submissions queue — approve appends the row into the array below */}
      {queue.length > 0 && (
        <div
          className="border border-amber-200 bg-amber-50/40 rounded-lg p-4 mb-6"
          data-testid="testimonial-queue"
        >
          <div className="flex items-center gap-2 mb-3">
            <Inbox className="w-4 h-4 text-amber-700" />
            <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-800">
              Pending review · {queue.length}
            </div>
          </div>
          <div className="space-y-2">
            {queue.map((sub) => {
              const busy = reviewingId === sub.id;
              return (
                <div
                  key={sub.id}
                  className="border border-amber-200 bg-white rounded-md p-3 flex items-start gap-3"
                  data-testid={`testimonial-queue-row-${sub.id}`}
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs text-white shrink-0"
                    style={{ background: "linear-gradient(135deg,#CBD5E1 0%,#94A3B8 100%)" }}
                  >
                    {sub.initials || "??"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">@{sub.user_handle}</span>
                      {sub.role && (
                        <span className="text-[11px] text-slate-500">· {sub.role}</span>
                      )}
                      <span className="text-[10px] text-slate-400 ml-auto tabular-nums">
                        {new Date(sub.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </span>
                    </div>
                    <p className="text-[13px] text-slate-700 leading-snug line-clamp-3">{sub.quote}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={busy}
                        onClick={() => approveSubmission(sub)}
                        className="h-8 px-3 text-xs text-white"
                        style={{ background: busy ? "#0F766E" : "#0D9488" }}
                        data-testid={`testimonial-queue-approve-${sub.id}`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => rejectSubmission(sub)}
                        className="h-8 px-3 text-xs border-rose-200 text-rose-700 hover:bg-rose-50"
                        data-testid={`testimonial-queue-reject-${sub.id}`}
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Rows */}
      <div className="space-y-4">
        {rows.map((row, i) => (
          <div
            key={row.id || row._id || `${row.quote || ""}-${i}`}
            className="border border-slate-200 rounded-lg p-4 bg-slate-50/40"
            data-testid={`testimonial-row-${i}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-500 tabular-nums">
                  {i + 1}
                </div>
                <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Entry</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="w-7 h-7 rounded-md border border-slate-200 hover:border-slate-400 flex items-center justify-center text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move up"
                  data-testid={`testimonial-row-${i}-up`}
                >
                  <GripVertical className="w-3.5 h-3.5 rotate-180" />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === rows.length - 1}
                  className="w-7 h-7 rounded-md border border-slate-200 hover:border-slate-400 flex items-center justify-center text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move down"
                  data-testid={`testimonial-row-${i}-down`}
                >
                  <GripVertical className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="w-7 h-7 rounded-md border border-rose-200 hover:bg-rose-50 flex items-center justify-center text-rose-600"
                  title="Delete"
                  data-testid={`testimonial-row-${i}-delete`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Quote</span>
              <Textarea
                value={row.quote}
                onChange={(e) => updateRow(i, "quote", e.target.value)}
                rows={3}
                placeholder="What did this user notice when they started tracking peptides + nutrition together?"
                className="mt-1 bg-white border-slate-200 text-slate-900 resize-y focus-visible:ring-0 focus-visible:border-[#0D9488]"
                data-testid={`testimonial-row-${i}-quote`}
              />
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Name</span>
                <Input
                  value={row.name}
                  onChange={(e) => updateRow(i, "name", e.target.value)}
                  placeholder="Marcus K."
                  className="mt-1 bg-white border-slate-200 text-slate-900 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                  data-testid={`testimonial-row-${i}-name`}
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Role / Protocol</span>
                <Input
                  value={row.role}
                  onChange={(e) => updateRow(i, "role", e.target.value)}
                  placeholder="Retatrutide protocol · 14-week cut"
                  className="mt-1 bg-white border-slate-200 text-slate-900 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                  data-testid={`testimonial-row-${i}-role`}
                />
              </label>
            </div>
            <label className="block mt-3 max-w-[6rem]">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Initials</span>
              <Input
                value={row.initials}
                onChange={(e) => updateRow(i, "initials", e.target.value.toUpperCase().slice(0, 3))}
                placeholder="MK"
                maxLength={3}
                className="mt-1 bg-white border-slate-200 text-slate-900 focus-visible:ring-0 focus-visible:border-[#0D9488] uppercase tracking-widest text-center font-bold"
                data-testid={`testimonial-row-${i}-initials`}
              />
            </label>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={addRow}
          className="border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
          data-testid="testimonial-add"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add testimonial
        </Button>
        <Button
          type="button"
          onClick={save}
          disabled={saving}
          className="text-white"
          style={{ background: saving ? TEAL_D : TEAL }}
          onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = TEAL_D; }}
          onMouseLeave={(e) => { if (!saving) e.currentTarget.style.background = TEAL; }}
          data-testid="testimonial-save"
        >
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      {/* Live preview */}
      <div className="mt-8 pt-6 border-t border-slate-200" data-testid="testimonial-preview">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-3">
          Live preview {previewList.length > 1 && `— ${previewList.length} rotating`}
        </div>
        {preview.quote ? (
          <div className="border border-slate-200 rounded-xl p-8 bg-white text-center">
            <Quote className="w-6 h-6 mx-auto mb-4 opacity-60" style={{ color: TEAL }} />
            <blockquote className="text-lg lg:text-xl font-medium leading-snug tracking-tight text-slate-900 max-w-2xl mx-auto">
              {preview.quote}
            </blockquote>
            <div className="mt-5 flex items-center justify-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs text-white"
                style={{ background: "linear-gradient(135deg,#CBD5E1 0%,#94A3B8 100%)" }}
              >
                {previewInitials}
              </div>
              <div className="text-left">
                <div className="text-sm font-semibold text-slate-900">{preview.name || "\u00A0"}</div>
                {preview.role && <div className="text-xs text-slate-500 mt-0.5">{preview.role}</div>}
              </div>
            </div>
            {previewList.length > 1 && (
              <div className="mt-4 flex items-center justify-center gap-1.5">
                {previewList.map((p, i) => (
                  <button
                    key={p?.id || `preview-dot-${i}`}
                    type="button"
                    onClick={() => setPreviewIdx(i)}
                    aria-label={`Preview testimonial ${i + 1}`}
                    className="rounded-full transition-all"
                    style={{
                      width: i === previewIdx ? 20 : 6,
                      height: 6,
                      background: i === previewIdx ? TEAL : "#CBD5E1",
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-sm text-slate-500 py-8 border border-dashed border-slate-200 rounded-xl">
            Add a quote to see the preview
          </div>
        )}
      </div>
    </div>
  );
}
